import { ActionIcon, Button, Group, Loader, Menu, Table, Text, Tooltip } from '@mantine/core';
import { IconDots, IconDownload, IconPlus } from '@tabler/icons-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import { useEmailDocument } from 'src/common/hooks/useEmailDocument';
import EmailDocumentButton from 'src/components/convenience/buttons/EmailDocumentButton';
import dataCaptureClasses from 'src/data-capture/DataCapture.module.css';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { useDeleteAgenda } from 'src/suitability-report/hooks/useDeleteAgenda';
import { useMeetingAgenda } from 'src/suitability-report/hooks/useMeetingAgendas';
import { GenericErrorPage } from '../../error/GenericErrorPage';
import PageLoader from '../../UI/loader/PageLoader';
import { useFileTemporaryLinkMutation } from '../hooks/useFileTemporaryLinkMutation';
import FileStatusIcon from './FileStatusIcon';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import FileUploadLayout from './FileUploadLayout';
import EmptyView from './components/EmptyView';
import FileNameWithIcon from './files-table/FileNameWithIcon';
import { dateMatchesSearch, formatDateTime } from 'src/meetings/utils/date-utils';

export default function MeetingAgendas() {
  const { meetingAgendas, isLoading, isError, error, refetch } = useMeetingAgenda();
  const { clientId, client } = useCurrentClient();
  const {
    createLink,
    isLoading: isCreatingLink,
    isError: creatingLinkFailed,
  } = useFileTemporaryLinkMutation();
  const {
    deleteMeetingAgenda,
    error: deleteAgendaError,
    reset: resetDeleteAgenda,
    isLoading: isDeletingAgenda,
  } = useDeleteAgenda();
  const { flagEnabledDocumentEdit } = useFlags();
  const navigate = useNavigate();

  const openAgendaFlow = () =>
    navigate(`/client/${clientId}/meeting-agenda`, { state: { fromPath: location.pathname } });
  const { handleEmailClick, handleDeleteEmail, isGenerating, isDeleting } = useEmailDocument();
  const [searchValue, setSearchValue] = useState('');
  const [agendaToDelete, setAgendaToDelete] = useState<{ id: string; fileName: string } | null>(
    null,
  );
  const [selectedAgendaForEmailGeneration, setSelectedAgendaForEmailGeneration] = useState<
    null | string
  >();

  const handleDeletePress = (agenda: { id: string; fileName?: string }) => {
    resetDeleteAgenda();
    setAgendaToDelete({ id: agenda.id, fileName: agenda.fileName || 'Untitled' });
  };

  const handleDownload = async (agendaFileId: number) => {
    try {
      const link = await createLink(agendaFileId);
      window.open(link.url, '_blank');
    } catch (e) {
      notifyAndLogError(
        'Failed to download meeting agenda',
        'An error occurred while trying to download the meeting agenda. Please try again later.',
        e,
        { clientId, agendaFileId },
      );
      return;
    }
  };

  const handleDeleteConfirm = async () => {
    if (!agendaToDelete) return;
    try {
      await deleteMeetingAgenda(agendaToDelete.id);
    } catch (e) {
      notifyAndLogError(
        'Failed to delete meeting agenda',
        'An error occurred while trying to delete the meeting agenda. Please try again later.',
        e,
        { clientId, reportId: agendaToDelete.id },
      );
    }
    setAgendaToDelete(null);
    await refetch();
  };

  const filteredMeetingAgendas = searchValue
    ? meetingAgendas.filter(agenda => {
        const searchLower = searchValue.toLowerCase();
        return (
          (agenda.fileName || '').toLowerCase().includes(searchLower) ||
          dateMatchesSearch(agenda.createdAt, searchLower)
        );
      })
    : meetingAgendas;

  const handleEmailGenerationClick = async (agendaId: string) => {
    setSelectedAgendaForEmailGeneration(agendaId);
    return await handleEmailClick(agendaId, 'MEETING_AGENDA');
  };

  const rows = filteredMeetingAgendas.map(agenda => (
    <Table.Tr key={agenda.id}>
      <Table.Td>
        <FileNameWithIcon
          fileName={agenda.fileName || 'No file'}
          isFolder={false}
          isGenerated={agenda.isGenerated}
        />
      </Table.Td>
      <Table.Td>
        <Text size="sm">{formatDateTime(agenda.createdAt)}</Text>
      </Table.Td>
      <Table.Td align="center">
        <FileStatusIcon
          status={agenda.backgroundProcess?.status ?? 'SUCCESS'}
          processingTooltipLabel="Generating..."
        />
      </Table.Td>
      <Table.Td align="right">
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs" wrap="nowrap">
            {flagEnabledDocumentEdit &&
              agenda.documentHistoryId &&
              (agenda.backgroundProcess?.status ?? 'SUCCESS') === 'SUCCESS' && (
                <Button
                  variant="primary"
                  onClick={() =>
                    navigate(`/client/${clientId}/document/meeting-agendas/${agenda.id}/edit`)
                  }
                >
                  Edit
                </Button>
              )}
            {agenda.fileId ? (
              <>
                <EmailDocumentButton
                  documentId={agenda.id}
                  isGenerating={isGenerating && selectedAgendaForEmailGeneration === agenda.id}
                  isDeleting={isDeleting}
                  onEmailClick={async () => await handleEmailGenerationClick(agenda.id)}
                  onDelete={handleDeleteEmail}
                  clientName={client ? `${client.firstName} ${client.lastName}` : undefined}
                  documentLabel={agenda.fileName}
                />
                <Tooltip label="Download">
                  <ActionIcon
                    variant={
                      flagEnabledDocumentEdit && agenda.documentHistoryId ? 'secondary' : 'primary'
                    }
                    size="lg"
                    onClick={() => handleDownload(agenda.fileId!)}
                  >
                    <IconDownload size={18} />
                  </ActionIcon>
                </Tooltip>
              </>
            ) : (
              <div />
            )}
          </Group>
          {agenda?.backgroundProcess?.status &&
            ['SUCCESS', 'FAILED'].includes(agenda?.backgroundProcess?.status) && (
              <Menu
                transitionProps={{ transition: 'pop-top-right' }}
                position="bottom-end"
                width={220}
                withinPortal
              >
                <Menu.Target>
                  <IconDots color="#475467" stroke={2} size={20} />
                </Menu.Target>

                <Menu.Dropdown>
                  <Menu.Item onClick={() => handleDeletePress(agenda)}>Delete</Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  if (isError || creatingLinkFailed) {
    return <GenericErrorPage error={error || { creatingLinkFailed }} />;
  }

  if (isCreatingLink) {
    return <PageLoader absolute={false} />;
  }

  if (meetingAgendas.length === 0 && !isLoading) {
    return (
      <FileUploadLayout title="Meeting agendas">
        <EmptyView
          title="Get ready for your next meeting"
          subtitle="Generate tailored meeting agendas with Siesta AI to keep discussions focused and valuable."
          ctaText="Generate new"
          ctaIcon={IconPlus}
          onCtaClick={openAgendaFlow}
        />
      </FileUploadLayout>
    );
  }

  return (
    <FileUploadLayout
      title="Meeting agendas"
      showSearch
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      actions={
        <>
          <Button
            variant="primary"
            leftSection={<IconPlus size={16} />}
            size="md"
            onClick={openAgendaFlow}
            visibleFrom="xl"
          >
            Generate
          </Button>
          <ActionIcon
            variant="filled"
            color="brand.6"
            size="lg"
            onClick={openAgendaFlow}
            hiddenFrom="xl"
          >
            <IconPlus size={16} />
          </ActionIcon>
        </>
      }
    >
      <Table.ScrollContainer
        minWidth={600}
        type="native"
        className={dataCaptureClasses.tableScrollContainer}
      >
        <Table
          variant="bare"
          captionSide="bottom"
          classNames={{ th: dataCaptureClasses.th }}
          verticalSpacing={'sm'}
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th>File name</Table.Th>
              <Table.Th>Created</Table.Th>
              <Table.Th align="center">Status</Table.Th>
              <Table.Th></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isLoading ? (
              <Table.Tr>
                <Table.Td colSpan={4} style={{ textAlign: 'center', padding: '2rem' }}>
                  <Loader size="sm" />
                </Table.Td>
              </Table.Tr>
            ) : (
              rows
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <ConfirmDeleteModal
        opened={!!agendaToDelete}
        onClose={() => setAgendaToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete meeting agenda"
        message={`Are you sure you want to delete "${agendaToDelete?.fileName}"? This action cannot be undone.`}
        error={deleteAgendaError}
        isLoading={isDeletingAgenda}
      />
    </FileUploadLayout>
  );
}
